import {
  addFreq,
  divFreq,
  multFreq,
  subFreq,
  toHz,
  toKiloHz,
  toMegaHz,
  toGigaHz,
  // @ts-ignore
} from 'hertzbel';
import { LoMode } from '../types/models/Snapshot';

const unitsArray = ['hz', 'khz', 'mhz', 'ghz'];

export const formatFreqPretty = (frequency: string) => {
  if (!frequency) return frequency;

  const hzString = toHz(frequency);
  const khzString = toKiloHz(frequency, 1);
  const mhzSTring = toMegaHz(frequency, 2);
  const ghzString = toGigaHz(frequency, 5);

  const freqArray = [hzString, khzString, mhzSTring, ghzString];

  return freqArray.reduce((a, b) => (a.length <= b.length ? a : b));
};

export const frequencyToIndex = (frequency: string) => {
  if (!frequency) return 0;
  const arrayIndex = frequency.toLowerCase();
  return unitsArray.indexOf(arrayIndex);
};

export const indexToFrequency = (unitIndex: number) => {
  return unitsArray[unitIndex];
};

export const freqSplit = (frequency: string) => {
  if (!frequency) return '';
  return frequency.split(' ');
};

export const freqValue = (frequency: string) => {
  if (!frequency) return '';
  return freqSplit(frequency)[0];
};

export const freqUnit = (frequency: string) => {
  return freqSplit(frequency)[1];
};

export const getCenterWithLo = (
  center: string,
  lo?: string,
  loMode?: LoMode
) => {
  let centerWithLo = center;
  if (lo) {
    if (loMode === 0) {
      centerWithLo = addFreq(center, lo);
    } else if (loMode === 1) {
      centerWithLo = subFreq(lo, center);
    }
  }

  return centerWithLo;
};

export const getCenterWithoutLo = (center: string, lo: string) => {
  const centerWithoutLo = subFreq(center, lo);
  const splitCenterWithoutLo = freqSplit(centerWithoutLo);
  const positiveCenterWithoutLo =
    Math.abs(Number(splitCenterWithoutLo[0])) + ' ' + splitCenterWithoutLo[1];
  return positiveCenterWithoutLo;
};

export const getCenterIndex = (
  numberOfPoints: number | undefined,
  type: number = 0
) => {
  if (!numberOfPoints) {
    if (type === 1) numberOfPoints = Math.floor(401 / 2);
    else numberOfPoints = Math.floor(601 / 2);
  }
  return Math.floor(numberOfPoints / 2);
};

export const getFrequencyArray = (
  center: string,
  span: string,
  numberOfPoints: number,
  lo?: string,
  loMode?: LoMode,
  includeUnit: boolean = true,
  type: number = 0
) => {
  if (!numberOfPoints) {
    if (type === 1) numberOfPoints = 401;
    else numberOfPoints = 601;
  }
  const centerWithLo = getCenterWithLo(center, lo, loMode);
  const splitFreq = freqSplit(centerWithLo);
  const absFreq =
    Math.abs(Number(splitFreq[0])).toFixed(2) + ' ' + splitFreq[1];

  let freqArray: string[] = [];
  for (let i = 0; i < numberOfPoints; i++) {
    let frequency = getFrequencyFromSnapshotIndex(
      absFreq,
      span,
      numberOfPoints,
      i
    );

    //Strip units if specified
    if (!includeUnit)
      frequency = Number(parseFloat(freqValue(frequency)).toFixed(2));

    freqArray.push(frequency);
  }
  return freqArray;
};

export const getFrequencyFromSnapshotIndex = (
  center: string,
  span: string,
  numberOfPoints: number,
  index: number
) => {
  const halfSpan = divFreq(span, 2, 8);
  const freqStart = subFreq(center, halfSpan);
  const freqPerPoint = divFreq(span, numberOfPoints - 1, 8);

  return addFreq(freqStart, multFreq(freqPerPoint, index, 8));
};

export const getIndexFromSnapshotFrequency = (
  center: string,
  span: string,
  numberOfPoints: number,
  frequency: string,
  lo?: string,
  loMode?: LoMode
) => {
  try {
    const centerWithLo = getCenterWithLo(center, lo, loMode);
    const freqInHz = toHz(frequency);
    const frequencyValue = freqValue(freqInHz);

    let foundIndex = -1;
    for (let i = 0; i < numberOfPoints; i++) {
      const freqAtPointInHz = toHz(
        getFrequencyFromSnapshotIndex(centerWithLo, span, numberOfPoints, i)
      );
      const freqAtPointValue = freqValue(freqAtPointInHz);

      if (Number(freqAtPointValue) > Number(frequencyValue)) {
        if (i === 0) {
          break;
        }
        foundIndex = i - 1;
        break;
      }

      if (Number(freqAtPointValue) === Number(frequencyValue)) {
        foundIndex = i;
        break;
      }
    }
    return foundIndex;
  } catch (e) {
    return -1;
  }
};
